// 体のつくりの型（影絵の関節模型）。ボスごとには寸法と持ち物だけ変え、型は使い回す（2026-11-03）。
//   人型（二刀・ドレス）と鳥と四つ足。2人組・布物は要る時に足す。
//   手前は明るく・奥は暗く塗る。関節は小さな点で見せる。

import { lerpV, type Pose, type Vec } from './core'

/** 模型の点 → 画面の点 */
export type ToScreen = (p: Pose, pt: Vec) => Vec

export interface MockupBody {
  id: string
  label: string
  /** 接地を見る足先の名前 */
  feet: string[]
  com(p: Pose): Vec
  /** 軌跡を描く先端（剣先・爪など）。模型の空間で返す */
  tip(p: Pose, name: string): Vec
  draw(ctx: CanvasRenderingContext2D, p: Pose, S: ToScreen, scale: number): void
}

export const MOCKUP_COLORS = {
  bg: '#1c2028', floor: '#3a4250', near: '#b9c0cc', nearHi: '#d3d8e0', far: '#6d7584', cloth: '#8f97a5',
  steel: '#f2f4f8', joint: '#2a303b', hero: '#ffffff', text: '#aab1bd', sub: '#7c8492',
  foot: '#5fc28a', com: '#e3c34a', path: '#69b6ff', hit: '#ef5b63', tell: '#c58a1a',
  ember: '#7fc4ff',
}
const C = MOCKUP_COLORS

function line(ctx: CanvasRenderingContext2D, a: Vec, b: Vec, w: number, col: string): void {
  ctx.strokeStyle = col; ctx.lineWidth = w; ctx.lineCap = 'round'
  ctx.beginPath(); ctx.moveTo(a[0], a[1]); ctx.lineTo(b[0], b[1]); ctx.stroke()
}
export function poly(ctx: CanvasRenderingContext2D, pts: Vec[], col: string): void {
  ctx.beginPath(); pts.forEach((q, i) => i ? ctx.lineTo(q[0], q[1]) : ctx.moveTo(q[0], q[1])); ctx.closePath()
  ctx.fillStyle = col; ctx.fill()
}
export function circ(ctx: CanvasRenderingContext2D, c: Vec, r: number, fill: string): void {
  ctx.beginPath(); ctx.arc(c[0], c[1], r, 0, Math.PI * 2); ctx.fillStyle = fill; ctx.fill()
}
function rotAbout(c: Vec, q: Vec, deg: number): Vec {
  const a = deg * Math.PI / 180, dx = q[0] - c[0], dy = q[1] - c[1]
  return [c[0] + dx * Math.cos(a) - dy * Math.sin(a), c[1] + dx * Math.sin(a) + dy * Math.cos(a)]
}
const V = (p: Pose, k: string): Vec => p[k] as Vec
const N = (p: Pose, k: string): number => p[k] as number

// ---------- 人型（二刀・ドレス）----------
//   点: hip chest head／手前の腕 eF hF・奥の腕 eB hB／手前の脚 kF fF・奥の脚 kB fB。角度: sF sB（剣の向き・度。0=前・90=下）。
export const HUMAN_STAND: Pose = {
  hip: [0, -74], chest: [3, -114], head: [4, -134], eF: [12, -94], hF: [16, -76], eB: [-6, -94], hB: [-4, -76],
  kF: [5, -38], fF: [7, 0], kB: [-1, -38], fB: [-2, 0], sF: -62, sB: -112, rx: 0, ry: 0,
}
const SWORD = 58
/** 主人公の細剣（模型の単位・11-04）。二刀より長く細い。 */
const RAPIER = 64

/** 人型。one＝手前の手だけに剣（主人公の細剣・奥の手は空手）。 */
function humanDual(dress: boolean, one?: { id: string; label: string; len: number }): MockupBody {
  const len = one?.len ?? SWORD
  return {
    id: one?.id ?? (dress ? 'human-dual-dress' : 'human-dual'), label: one?.label ?? (dress ? '人型・二刀・ドレス' : '人型・二刀'),
    feet: ['fF', 'fB'],
    com: p => lerpV(V(p, 'hip'), V(p, 'chest'), 0.35),
    tip(p, name) {
      const h = V(p, name === 'sF' ? 'hF' : 'hB'), a = N(p, name) * Math.PI / 180
      return [h[0] + Math.cos(a) * len, h[1] + Math.sin(a) * len]
    },
    draw(ctx, p, S, s) {
      const q = (k: string) => S(p, V(p, k))
      // 肩・腰の左右の点があればそこから腕・脚を出し、胴は4点の面で描く（ひねると胴の幅が変わって見える・11-03）
      const has = (k: string) => Array.isArray(p[k])
      const shF = has('shF') ? q('shF') : q('chest'), shB = has('shB') ? q('shB') : q('chest')
      const hpF = has('hpF') ? q('hpF') : q('hip'), hpB = has('hpB') ? q('hpB') : q('hip')
      const tF = S(p, this.tip(p, 'sF')), tB = S(p, this.tip(p, 'sB'))
      line(ctx, shB, q('eB'), 9 * s, C.far); line(ctx, q('eB'), q('hB'), 8 * s, C.far); if (!one) line(ctx, q('hB'), tB, 2.6, C.steel)
      // 足首（anF・anB）があれば脛は足首まで、足首→つま先を足の甲として描く（つま先立ちが見える・11-04）
      const leg = (hp: Vec, k: string, an: string, f: string, col: string) => {
        line(ctx, hp, q(k), 10 * s, col)
        if (has(an)) { line(ctx, q(k), q(an), 8 * s, col); line(ctx, q(an), q(f), 5 * s, col) } else line(ctx, q(k), q(f), 8 * s, col)
      }
      leg(hpB, 'kB', 'anB', 'fB', C.far)
      leg(hpF, 'kF', 'anF', 'fF', C.near)
      if (dress) {
        const waist = lerpV(q('chest'), q('hip'), 0.55), hem = lerpV(q('hip'), lerpV(q('kF'), q('kB'), 0.5), 0.85)
        poly(ctx, [[waist[0] - 9 * s, waist[1]], [waist[0] + 9 * s, waist[1]], [hem[0] + 22 * s, hem[1]], [hem[0] - 22 * s, hem[1]]], C.cloth)
      }
      if (has('shF')) {
        ctx.lineJoin = 'round'; ctx.strokeStyle = C.near; ctx.lineWidth = 16 * s
        poly(ctx, [shF, shB, hpB, hpF], C.near); ctx.stroke()
        line(ctx, shB, shF, 5 * s, C.nearHi) // 肩の線
      } else line(ctx, q('chest'), q('hip'), 22 * s, C.near)
      circ(ctx, q('head'), 11 * s, C.near)
      line(ctx, shF, q('eF'), 9 * s, C.nearHi); line(ctx, q('eF'), q('hF'), 8 * s, C.nearHi); line(ctx, q('hF'), tF, one ? 2 : 2.6, C.steel)
      for (const k of ['eF', 'hF', 'eB', 'hB', 'kF', 'kB', 'hip']) circ(ctx, q(k), 2.2, C.joint)
      // 奥の手の指（nUp＝立っている本数 0〜5・先代の姫の指折り・11-04）。前腕の先へ扇に5本、折った指は短い根元だけ。
      //   遠くからでも本数が読めるよう、奥の手でも明るい色で一番上に描く。
      if (typeof p.nUp === 'number') {
        const e = q('eB'), h = q('hB'), a0 = Math.atan2(h[1] - e[1], h[0] - e[0])
        circ(ctx, h, 5 * s, C.nearHi)
        for (let i = 0; i < 5; i++) {
          const up = Math.max(0, Math.min(1, N(p, 'nUp') - i)), a = a0 + (i - 2) * 0.4, L = (4 + 14 * up) * s
          line(ctx, h, [h[0] + Math.cos(a) * L, h[1] + Math.sin(a) * L], 3.6 * s, up > 0.5 ? C.steel : C.far)
        }
      }
      // 数える光（l0〜l2＝位置・la0〜la2＝明るさ 0〜1・先代の姫の指折り案 C・11-04）。青いにじみと白い芯の2重の丸。
      for (let i = 0; i < 3; i++) {
        const a = p['la' + i]
        if (typeof a !== 'number' || a <= 0.01 || !has('l' + i)) continue
        const c = q('l' + i)
        ctx.globalAlpha = 0.35 * a; circ(ctx, c, 9 * s, C.ember)
        ctx.globalAlpha = a; circ(ctx, c, 4 * s, C.ember); circ(ctx, c, 2 * s, C.hero)
        ctx.globalAlpha = 1
      }
    },
  }
}

/** 人型を部位の数で組む（11-03・全身で動かす技用）。足先は床の上の位置（技の原点から）で受け、膝は前へ曲げて解く。
 *  px・py＝腰の位置／lean＝胴の前傾（度）／tw＝肩のひねり（＋で手前の肩が前）・ht＝腰のひねり／
 *  aF bF sF＝手前の二の腕・前腕・剣の角度（0＝前・90＝下）、aB bB sB＝奥／fF fB＝足先。
 *  rF rB（省略可・0〜1）＝かかとの上げ具合。渡すと fF fB はつま先になり、足首を上げて足の甲を描く（0＝べた足・1＝つま先で立つ／宙ではつま先を伸ばす）。 */
export interface HumanRig {
  px: number; py: number; lean: number; tw: number; ht: number
  aF: number; bF: number; sF: number; aB: number; bB: number; sB: number
  fF: Vec; fB: Vec
  rF?: number; rB?: number
}
export const HUMAN_RIG = { torso: 40, neck: 20, upper: 21, fore: 19, thigh: 37, shin: 37, shoulder: 10, hipW: 6, foot: 9 }
const D2R = Math.PI / 180
const dir = (deg: number, len: number): Vec => [Math.cos(deg * D2R) * len, Math.sin(deg * D2R) * len]
const add = (a: Vec, b: Vec): Vec => [a[0] + b[0], a[1] + b[1]]
function knee(hj: Vec, f: Vec): Vec {
  const L = HUMAN_RIG.thigh, dx = f[0] - hj[0], dy = f[1] - hj[1], len = Math.hypot(dx, dy) || 1, d = Math.min(len, L * 2 - 0.01)
  const h = Math.sqrt(Math.max(0, L * L - (d / 2) * (d / 2))), ux = dx / len, uy = dy / len
  const m = lerpV(hj, f, 0.5), nx = -uy, ny = ux // 前（＋x）へ曲げる
  return nx >= 0 ? [m[0] + nx * h, m[1] + ny * h] : [m[0] - nx * h, m[1] - ny * h]
}
export function humanPose(r: HumanRig): Pose {
  const R = HUMAN_RIG, hip: Vec = [0, r.py]
  const chest = add(hip, [Math.sin(r.lean * D2R) * R.torso, -Math.cos(r.lean * D2R) * R.torso])
  const head = add(chest, [Math.sin(r.lean * 0.5 * D2R) * R.neck, -Math.cos(r.lean * 0.5 * D2R) * R.neck]) // 頭は胴ほど傾けない（目は相手から外さない）
  const sw = Math.sin(r.tw * D2R) * R.shoulder, hw = Math.sin(r.ht * D2R) * R.hipW
  const shF: Vec = [chest[0] + sw, chest[1] + 1], shB: Vec = [chest[0] - sw, chest[1] - 1]
  const hpF: Vec = [hip[0] + hw, hip[1]], hpB: Vec = [hip[0] - hw, hip[1]]
  const eF = add(shF, dir(r.aF, R.upper)), hF = add(eF, dir(r.bF, R.fore))
  const eB = add(shB, dir(r.aB, R.upper)), hB = add(eB, dir(r.bB, R.fore))
  const fF: Vec = [r.fF[0] - r.px, r.fF[1]], fB: Vec = [r.fB[0] - r.px, r.fB[1]]
  const base = { hip, chest, head, shF, shB, hpF, hpB, eF, hF, eB, hB, fF, fB, sF: r.sF, sB: r.sB, rx: r.px, ry: 0 }
  if (r.rF == null && r.rB == null) return { ...base, kF: knee(hpF, fF), kB: knee(hpB, fB) }
  // つま先から足首を出す：べた足は足首がつま先の後ろの床の高さ、かかとを上げるほど足首はつま先の真上へ
  const ankle = (toe: Vec, rel: number): Vec => { const a = (8 + 74 * rel) * D2R; return [toe[0] - Math.cos(a) * R.foot, toe[1] - Math.sin(a) * R.foot] }
  const anF = ankle(fF, r.rF ?? 0), anB = ankle(fB, r.rB ?? 0)
  return { ...base, anF, anB, kF: knee(hpF, anF), kB: knee(hpB, anB) }
}

// ---------- 鳥 ----------
//   点: body sh(翼の付け根) nb(首の付け根) head tail wN wF(翼の先・手前/奥) hip kN fN kF fF。角度: bang（胴の傾き）。数: beak（嘴の開き 0〜1）。
export const BIRD_STAND: Pose = {
  body: [-4, -86], bang: 12, sh: [4, -96], nb: [22, -100], head: [32, -128], beak: 0, tail: [-52, -122], wN: [-40, -96], wF: [-36, -104],
  hip: [-2, -66], kN: [-8, -36], fN: [8, 0], kF: [-12, -36], fF: [-10, 0], rx: 0, ry: 0,
}
const wingPoly = (sh: Vec, tp: Vec): Vec[] => [sh, rotAbout(sh, tp, 16), tp, rotAbout(sh, tp, -12), lerpV(sh, rotAbout(sh, tp, -30), 0.55)]
function claws(ctx: CanvasRenderingContext2D, f: Vec, s: number, col: string): void {
  for (const d of [-1, 0, 1]) line(ctx, f, [f[0] - 8 * s, f[1] + d * 3 * s], 3, col)
}

const BIRD: MockupBody = {
  id: 'bird', label: 'とり', feet: ['fN', 'fF'],
  com: p => V(p, 'body'),
  tip: (p, name) => V(p, name),
  draw(ctx, p, S, s) {
    const q = (k: string) => S(p, V(p, k))
    poly(ctx, wingPoly(q('sh'), q('wF')), C.far)
    line(ctx, q('hip'), q('kF'), 7 * s, C.far); line(ctx, q('kF'), q('fF'), 5 * s, C.far); claws(ctx, q('fF'), s, C.far)
    const body = q('body'), tail = q('tail')
    poly(ctx, [lerpV(body, tail, 0.3), rotAbout(body, tail, -14), tail, rotAbout(body, tail, 12)], '#5d6572')
    ctx.save(); ctx.translate(body[0], body[1]); ctx.rotate(N(p, 'bang') * Math.PI / 180)
    ctx.beginPath(); ctx.ellipse(0, 0, 38 * s, 26 * s, 0, 0, Math.PI * 2); ctx.fillStyle = C.near; ctx.fill(); ctx.restore()
    const h = q('head')
    line(ctx, q('nb'), h, 15 * s, C.near); circ(ctx, h, 10 * s, C.near)
    poly(ctx, [[h[0] - 2 * s, h[1] - 9 * s], [h[0] + 6 * s, h[1] - 15 * s], [h[0] + 9 * s, h[1] - 7 * s]], C.nearHi)
    const o = N(p, 'beak') * 0.5
    const bk: Vec[] = [[h[0] - 8 * s, h[1] - 3 * s], [h[0] - 20 * s, h[1] + 1 * s - o * 6 * s], [h[0] - 8 * s, h[1] + 4 * s], [h[0] - 18 * s, h[1] + 5 * s + o * 8 * s]]
    poly(ctx, [bk[0]!, bk[1]!, bk[2]!], C.steel); poly(ctx, [bk[2]!, bk[3]!, [bk[2]![0], bk[2]![1] + 2 * s]], C.steel)
    line(ctx, q('hip'), q('kN'), 7 * s, C.nearHi); line(ctx, q('kN'), q('fN'), 5 * s, C.nearHi); claws(ctx, q('fN'), s, C.nearHi)
    poly(ctx, wingPoly(q('sh'), q('wN')), '#a3abb8')
    for (const k of ['hip', 'kN', 'kF', 'sh', 'nb']) circ(ctx, q(k), 2.2, C.joint)
  },
}


// ---------- 四つ足（獅子の形・戴冠獣）----------
//   点: wit(肩甲骨の上＝き甲) hip(股関節) neck head tail0..2／前脚 jF?(肩の関節) eF?(肘) wF?(手首) pF?(前の足先)／
//   後脚 kH?(膝) hH?(踵) pH?(後の足先)。? は N＝手前・F＝奥。角度: nod（頭の傾き・度）。
//   pel（腰骨の上）がある時は hip が股関節で、腰骨（pel→hip）を描き、胴は pel を基準に描く。
//   歩きの周期は moves/hound_walk.ts が計算する（立ち姿もそこから取る）。
function quadLeg(ctx: CanvasRenderingContext2D, pts: Vec[], widths: number[], col: string, s: number): void {
  for (let i = 0; i < pts.length - 1; i++) line(ctx, pts[i]!, pts[i + 1]!, widths[i]! * s, col)
  const paw = pts[pts.length - 1]!
  ctx.beginPath(); ctx.ellipse(paw[0], paw[1] - 4 * s, 9 * s, 5 * s, 0, 0, Math.PI * 2); ctx.fillStyle = col; ctx.fill()
}
/** 点を通る閉じた滑らかな形（中点を通る2次曲線） */
function blob(ctx: CanvasRenderingContext2D, pts: Vec[], col: string): void {
  const n = pts.length, mid = (i: number): Vec => lerpV(pts[i % n]!, pts[(i + 1) % n]!, 0.5)
  ctx.beginPath(); const m0 = mid(n - 1); ctx.moveTo(m0[0], m0[1])
  for (let i = 0; i < n; i++) { const c = pts[i]!, m = mid(i); ctx.quadraticCurveTo(c[0], c[1], m[0], m[1]) }
  ctx.closePath(); ctx.fillStyle = col; ctx.fill()
}

const QUAD: MockupBody = {
  id: 'quad', label: '四つ足', feet: ['pHN', 'pFN', 'pHF', 'pFF'],
  com: p => { const a = V(p, 'wit'), b = V(p, 'hip'); return [b[0] + (a[0] - b[0]) * 0.6, (a[1] + b[1]) / 2 + 20] },
  tip: (p, name) => V(p, name),
  draw(ctx, p, S, s) {
    const q = (k: string) => S(p, V(p, k))
    const rel = (k: string, dx: number, dy: number) => S(p, [V(p, k)[0] + dx, V(p, k)[1] + dy])
    // 胴の後ろの基準：腰骨があれば腰骨の上から、無ければ hip から
    const pel = p.pel ? V(p, 'pel') : null
    const back = (dx: number, dy: number) => pel ? S(p, [pel[0] - 6 + dx, pel[1] + 26 + dy]) : rel('hip', dx, dy)
    // 奥の脚
    quadLeg(ctx, [q('hip'), q('kHF'), q('hHF'), q('pHF')], [20, 12, 9], C.far, s)
    line(ctx, q('wit'), q('jFF'), 16 * s, C.far)
    quadLeg(ctx, [q('jFF'), q('eFF'), q('wFF'), q('pFF')], [16, 12, 9], C.far, s)
    // 尾
    ctx.strokeStyle = C.cloth; ctx.lineWidth = 6 * s; ctx.lineCap = 'round'
    const t0 = q('tail0'), t1 = q('tail1'), t2 = q('tail2')
    ctx.beginPath(); ctx.moveTo(t0[0], t0[1]); ctx.quadraticCurveTo(t1[0], t1[1], t2[0], t2[1]); ctx.stroke()
    circ(ctx, t2, 7 * s, C.cloth)
    // 胴（き甲・背・腰・腹・胸）
    blob(ctx, [rel('wit', 26, 34), rel('wit', 4, -8), back(40, -28), back(-6, -26), back(-24, -6),
      back(-18, 22), back(22, 34), rel('wit', -40, 66), rel('wit', 10, 72), rel('wit', 30, 54)], C.near)
    // たてがみと頭（王冠の歯）
    const hd = q('head'), nk = q('neck')
    blob(ctx, [rel('wit', -14, -14), rel('neck', 10, -30), rel('head', 0, -24), rel('head', 4, 20), rel('neck', 14, 36), rel('wit', 10, 50)], C.cloth)
    ctx.save(); ctx.translate(hd[0], hd[1]); ctx.rotate(-N(p, 'nod') * Math.PI / 180)
    ctx.beginPath(); ctx.ellipse(-8 * s, 4 * s, 24 * s, 17 * s, 0, 0, Math.PI * 2); ctx.fillStyle = C.nearHi; ctx.fill()
    for (let i = 0; i < 4; i++) poly(ctx, [[(-14 + i * 9) * s, -12 * s], [(-10 + i * 9) * s, -30 * s], [(-6 + i * 9) * s, -12 * s]], C.steel)
    ctx.restore()
    void nk
    // 手前の脚（肩甲骨ごと）
    line(ctx, q('wit'), q('jFN'), 18 * s, C.nearHi)
    quadLeg(ctx, [q('jFN'), q('eFN'), q('wFN'), q('pFN')], [17, 12, 9], C.nearHi, s)
    if (pel) line(ctx, q('pel'), q('hip'), 20 * s, C.nearHi)
    quadLeg(ctx, [q('hip'), q('kHN'), q('hHN'), q('pHN')], [24, 13, 9], C.nearHi, s)
    for (const k of [...(pel ? ['pel'] : []), 'wit', 'jFN', 'eFN', 'wFN', 'hip', 'kHN', 'hHN']) circ(ctx, q(k), 2.2, C.joint)
  },
}

export const MOCKUP_BODIES: Record<string, MockupBody> = {
  'human-dual-dress': humanDual(true),
  'human-dual': humanDual(false),
  'human-rapier-skirt': humanDual(true, { id: 'human-rapier-skirt', label: '人型・細剣・スカート（主人公）', len: RAPIER }),
  bird: BIRD,
  quad: QUAD,
}
